import {ExternalCompletionDetails} from "./ExternalCompletion";
import {FailureEvidenceList} from "./MerchantCancellation";
import React, {useEffect, useMemo, useRef, useState} from "react";
import type {Payment, PaymentActionRequest, PayinRequisites} from "../types";
import {downloadClientReceipt, downloadReceipt, publishExchangeSellAddress} from "../api";
import type {ExchangeSellPanelState} from "../api";
import {useAuth} from "../auth";
import {
    formatAmount,
    formatCard,
    formatDate,
    formatFixedRate,
    formatRubles,
    orderAmountProblem,
    resolveEffectiveRate,
    statusClass,
    traderAmountRangeText,
    traderPayinTerms,
    traderTermsLines,
    transferTermsMet,
} from "../utils";
import {rublesToKopecks} from "../utils/amount";
import {CountdownBadge, ORDER_DEADLINE_LABEL, SOURCE_DEADLINE_LABEL} from "./CountdownBadge";
import {
    analyzeAmount,
    analyzeRate,
    buildActionRequest,
    buildCancelOrderRequest,
    cancelOrderBlockedReason,
    cancelOrderConfirmReady,
    cancelOrderRequisiteLast4,
    closedAtSourceNotice,
    exchangeCompleteBlockedReason,
    exchangePayoutOpen,
    isExchangeBuyOrder,
    isExchangeSellOrder,
    exchangeDataMissingNotice,
    directionLabel,
    equalPayinParts,
    formatActionError,
    formatCancelOrderError,
    formatRubInput,
    getDisabledReason,
    getRemainingAmount,
    completionInputAmount,
    initialPayinRequisites,
    isClaimTimeoutAppeal,
    merchantPortalProofReady,
    normalizePayinRequisites,
    payoutSentBlockedReason,
    rejectFile,
    returnToPoolBlockedReason,
    validatePayinRequisites,
} from "./payment-details";
import type {
    AmountAnalysis,
    ConfirmAction,
    RateAnalysis,
    ReceiptDraft,
} from "./payment-details";
import {WorkflowStages} from "./payment-details/WorkflowStages";
import {selectActiveSellState, sellCompletionProofReady} from "./payment-details/ExchangeSell";
import {SellDisputeButton} from "./payment-details/SellDisputeButton";
import {SectionTitle} from "./payment-details/WorkflowStageControls";
import {PortalReceiptList} from "./PortalReceiptList";
import {isExternalWorkflow, isPayinWorkflow, isSupportedWorkflow} from "./payment-details/workflow";

interface PaymentDetailsPanelProps {
    payment: Payment;
    onClose: () => void;
    onSubmit: (request: PaymentActionRequest) => Promise<void>;
    onExpire?: () => void;
}

export function PaymentDetailsPanel({payment, onClose, onSubmit, onExpire}: PaymentDetailsPanelProps): JSX.Element {
    const {token} = useAuth();
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [receipts, setReceipts] = useState<ReceiptDraft[]>([]);
    // Список нужен и в очистке, которая ничего не знает о текущем рендере:
    // объектные ссылки надо отозвать, иначе файлы держатся в памяти вкладки.
    const receiptsRef = useRef<ReceiptDraft[]>([]);
    const [receiptError, setReceiptError] = useState("");
    // Сумма закрытия — весь остаток заявки и только он (UOF-02). Поле держит
    // именно остаток, а не полную сумму: заявки, частично оплаченные до этой
    // правки, обязаны закрываться на то, что по ним не закрыто.
    const [amountInput, setAmountInput] = useState(() => formatRubInput(completionInputAmount(payment)));
    // Для фикс-заявки поле курса заполнено серверным значением и заблокировано:
    // бэк всё равно проведёт платёж по нему, что бы сюда ни попало.
    const [rateInput, setRateInput] = useState(resolveEffectiveRate(payment.rateFixed).value);
    const [requisitesDraft, setRequisitesDraft] = useState<PayinRequisites>(() => initialPayinRequisites(payment));
    // Сумма заявки, которую трейдер выбирает из диапазона мерчанта. Сначала —
    // верхняя граница: именно её сервер держит за сумму заявки до выбора.
    const [orderAmountInput, setOrderAmountInput] = useState(() => formatRubInput(getRemainingAmount(payment)));
    const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);
    // Подтверждение терминальной отмены. Два поля вместо одного клика — это и
    // есть та компенсация, на которой владелец снял прежний запрет трейдерской
    // отмены, поэтому они живут состоянием панели, а не значениями по умолчанию
    // внутри модалки: отказ сервера её не закрывает, и введённое обязано
    // пережить отказ.
    const [cancelLast4, setCancelLast4] = useState("");
    const [cancelAmountInput, setCancelAmountInput] = useState("");
    // Hash транзакции USDT-этапа. Живёт состоянием панели по той же причине,
    // что и два поля отмены: отказ сервера модалку не закрывает, и введённое
    // обязано пережить отказ — набирать 64 символа заново человек не станет.
    const [payoutTxHash, setPayoutTxHash] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [downloadingReceipt, setDownloadingReceipt] = useState(false);
    // Открываемый сейчас чек клиента и ошибка его открытия. Отдельно от
    // downloadingReceipt: тот про единственный чек трейдера, а здесь чеков
    // несколько и «занят» ровно один из них.
    const [openingClientReceipt, setOpeningClientReceipt] = useState<string | null>(null);
    const [clientReceiptError, setClientReceiptError] = useState("");
    const [submitError, setSubmitError] = useState("");

    const paymentKey = payment.paymentId ?? payment.orderId;
    // Срок площадки — у заявок кабинета и только у них. Он и есть бронь такой
    // заявки, поэтому здесь показывается вместо неё, а не рядом.
    const supportedWorkflow = isSupportedWorkflow(payment.workflowKind);
    const externalWorkflow = isExternalWorkflow(payment.workflowKind);
    const sourceDeadline = externalWorkflow ? (payment.claimNotAfter ?? null) : null;
    const claimTimeoutAppeal = isClaimTimeoutAppeal(payment);
    // Свой срок заявки. После системной timeout-апелляции момент срока остаётся
    // историческим фактом, а не поводом рисовать отрицательный отсчёт: работа
    // закреплена за тем же трейдером до решения человека.
    const workDeadline = payment.status.toLowerCase() === "pending" && !sourceDeadline && !claimTimeoutAppeal && !isExchangeSellOrder(payment)
        ? (payment.expiresAt ?? null)
        : null;
    const isIncome = isPayinWorkflow(payment.workflowKind);
    const hasPayinRequisites = Boolean(payment.payinRequisites);
    // Курс, зафиксированный при создании заявки, или null у обычных заявок,
    // где курс по-прежнему вводит трейдер.
    const effectiveRate = resolveEffectiveRate(payment.rateFixed);
    const fixedRate = effectiveRate.isFixed ? effectiveRate.value : null;
    const fixedRateLabel = formatFixedRate(payment);
    const exchangeSell = isExchangeSellOrder(payment);
    const sellState = exchangeSell ? (payment.exchange as unknown as ExchangeSellPanelState | undefined) ?? null : null;
    const [publishedSellState, setPublishedSellState] = useState<ExchangeSellPanelState | null>(null);
    const [publishingSellAddress, setPublishingSellAddress] = useState(false);
    const [sellPublishError, setSellPublishError] = useState("");

    useEffect(() => {
        receiptsRef.current.forEach((draft) => URL.revokeObjectURL(draft.objectUrl));
        setReceipts([]);
        setReceiptError("");
        setAmountInput(formatRubInput(completionInputAmount(payment)));
        setRateInput(resolveEffectiveRate(payment.rateFixed).value);
        setRequisitesDraft(initialPayinRequisites(payment));
        setOrderAmountInput(formatRubInput(getRemainingAmount(payment)));
        setConfirmAction(null);
        setCancelLast4("");
        setCancelAmountInput("");
        setPayoutTxHash("");
        setSubmitting(false);
        setDownloadingReceipt(false);
        setOpeningClientReceipt(null);
        setClientReceiptError("");
        setSubmitError("");
        setPublishedSellState(null);
        setSellPublishError("");
    }, [paymentKey]);

    // The selected payment can receive a fresh server snapshot without being
    // remounted (for example, after an amount revision). The amount field is
    // read-only and represents the current remaining amount, so keep it in
    // sync with that snapshot while leaving receipt drafts untouched.
    const completionAmount = completionInputAmount(payment);
    useEffect(() => {
        setAmountInput(formatRubInput(completionAmount));
    }, [paymentKey, completionAmount]);

    useEffect(() => {
        setRequisitesDraft(initialPayinRequisites(payment));
    }, [
        paymentKey,
        payment.payinRequisites?.cardNumber,
        payment.payinRequisites?.cardHolder,
        payment.payinRequisites?.bankName,
        payment.payinRequisites?.bankCode,
        payment.payinRequisites?.payment,
    ]);

    useEffect(() => {
        receiptsRef.current = receipts;
    }, [receipts]);

    useEffect(() => {
        return () => {
            receiptsRef.current.forEach((draft) => URL.revokeObjectURL(draft.objectUrl));
        };
    }, []);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key !== "Escape") return;
            if (submitting) return;
            if (confirmAction) {
                setConfirmAction(null);
                return;
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [confirmAction, onClose, submitting]);

    const amountAnalysis = useMemo(() => analyzeAmount(payment, amountInput), [payment, amountInput]);
    const rateAnalysis = useMemo(() => analyzeRate(rateInput), [rateInput]);
    // Receipt is mandatory before complete, but a receipt already attached to the
    // payment (payment.receiptId) satisfies it without re-uploading.
    const hasReceipt = receipts.length > 0 || Boolean(payment.receiptId);
    // Условия мерчанта по входящей заявке: диапазон суммы, число переводов,
    // минимум одного. Сумму из диапазона трейдер выбирает ровно один раз — при
    // выдаче реквизитов; после неё поле исчезает, а сумма заявки уже выбранная.
    const payinTerms = isIncome ? traderPayinTerms(payment) : null;
    const choosingAmount = !hasPayinRequisites && payinTerms?.range != null;
    const orderAmountKopecks = rublesToKopecks(orderAmountInput);
    const orderAmountIssue = choosingAmount && payinTerms ? orderAmountProblem(payinTerms, orderAmountKopecks) : "";
    const requisitesTotal = choosingAmount ? (orderAmountKopecks ?? getRemainingAmount(payment)) : getRemainingAmount(payment);
    const requisitesValid =
        validatePayinRequisites(requisitesDraft, requisitesTotal) &&
        (!choosingAmount || (orderAmountKopecks !== null && !orderAmountIssue)) &&
        transferTermsMet(payinTerms, requisitesDraft.parts);
    const canSaveRequisites = isIncome && !hasPayinRequisites && requisitesValid && !submitting;
    // Смена суммы пересчитывает части поровну на то же число частей: сумма частей
    // обязана сходиться с выбранной. Один реквизит на всю сумму делить нечего.
    const changeOrderAmount = (raw: string) => {
        setOrderAmountInput(raw);
        const kopecks = rublesToKopecks(raw);
        if (kopecks !== null && payinTerms?.transferCount != null) {
            setRequisitesDraft((previous) => previous.parts?.length ? equalPayinParts(previous.parts.length, kopecks, previous.parts) : previous);
        }
    };
    // У фикс-заявки курс не вводится, поэтому и придираться к нему нечего:
    // сервер возьмёт свой.
    const rateReady = fixedRate != null || rateAnalysis.ok;
    const actionLabel = isIncome ? "Деньги получены" : "Пометить выполненной";
    const returnToPoolBlocked = returnToPoolBlockedReason(payment);
    // Заявка, закрытая на площадке (В-31). Карточка говорит об этом и чем именно,
    // а действий по ней нет ни одного: ни закрыть, ни вернуть в пул, ни открыть
    // спор — площадка уже всё решила, и разбирать нечего.
    const closedAtSource = closedAtSourceNotice(payment);
    // Заявка-обмен без второй ноги: адреса клиента нет, значит вторую половину
    // сделки закрыть нечем. Денежные кнопки гаснут так же, как у
    // неподдерживаемого сценария — рубли по такой заявке закрывать нельзя.
    const exchangeDataMissing = exchangeDataMissingNotice(payment);
    // У покупки USDT чек трейдера не участвует в закрытии вовсе: его заменяет
    // чек клиента, и он же открывает кнопку. Все остальные сценарии проверяются
    // ровно как раньше.
    const exchangeBuy = isExchangeBuyOrder(payment);
    const waitingForClientReceipt = exchangeCompleteBlockedReason(payment);
    const activeSellState = selectActiveSellState(sellState, publishedSellState);
    const sellReviewBlocking = exchangeSell && Boolean(activeSellState?.reviewBlocking);
    const proofReady = exchangeBuy ? waitingForClientReceipt === "" : exchangeSell ? sellCompletionProofReady(activeSellState, hasReceipt) : payment.merchantPortalIn === true ? merchantPortalProofReady(payment) : hasReceipt;
    const terminalPayment = ["success", "cancelled"].includes(payment.status.toLowerCase());
    const canComplete = !terminalPayment && supportedWorkflow && !closedAtSource && !exchangeDataMissing && !sellReviewBlocking && proofReady && amountAnalysis.ok && rateReady && (!isIncome || hasPayinRequisites);
    const disabledReason = supportedWorkflow
        ? getDisabledReason(hasReceipt, amountAnalysis, rateAnalysis, isIncome, hasPayinRequisites, fixedRate, payment)
        : "Сценарий не поддерживается";
    // Отмена заявки — четвёртое финальное действие (28.08.2026). Метка спора её
    // не блокирует, заявку кабинета — тоже: закрыть у нас и сказать площадке мы
    // умеем и там.
    // Обе кнопки отмены гасит одна и та же причина — сигналы клиента, — и
    // спрашивают её обе функции ниже первым делом. Отдельной переменной здесь
    // нет намеренно: второй источник той же причины разошёлся бы с ними.
    const cancelOrderBlocked = cancelOrderBlockedReason(payment, receipts.length);
    // Последние 4 цифры реквизита нужны там, где реквизит есть. Пусто —
    // подтверждение одной суммой, и поле в модалке не показывается вовсе.
    const cancelRequisiteLast4 = cancelOrderRequisiteLast4(payment);
    const cancelRequisiteRequired = cancelRequisiteLast4 != null;
    // Открытый USDT-этап: рубли закрыты, перевод клиенту ещё нет. В этом
    // состоянии заявка остаётся в работе, но первых трёх кнопок футера у неё
    // уже нет — платёж терминален, и сервер откажет любой из них. Живым
    // остаётся спор: разбирать по такой заявке как раз есть что.
    const payoutStageOpen = exchangePayoutOpen(payment);
    const payoutBlocked = payoutStageOpen ? payoutSentBlockedReason(payment, payoutTxHash) : "";
    const publishSellAddress = async (): Promise<void> => {
        if (!token || publishingSellAddress) return;
        setPublishingSellAddress(true);
        setSellPublishError("");
        try {
            const response = await publishExchangeSellAddress(payment.paymentId ?? payment.orderId, token);
            if (response.exchange) setPublishedSellState(response.exchange);
            else onExpire?.();
        } catch (err) {
            setSellPublishError(formatActionError(err));
        } finally {
            setPublishingSellAddress(false);
        }
    };

    const toDraft = (file: File): ReceiptDraft => ({
        file,
        fileName: file.name,
        fileSize: file.size,
        // Пустой mime у выбранного файла — обычное дело; заменять его на
        // application/pdf теперь нельзя: чек может быть картинкой, и подпись
        // «PDF» на скриншоте врала бы.
        mimeType: file.type,
        objectUrl: URL.createObjectURL(file),
    });

    // У заявки кабинета чеки накапливаются: трейдер выбирает файлы по одному или
    // пачкой, и каждый следующий добавляется к уже выбранным. У обычной заявки
    // чек по-прежнему один и новый выбор заменяет старый — её путь не меняется.
    const handleFiles = (files: FileList | null) => {
        if (sellReviewBlocking) return;
        if (!files || files.length === 0) return;
        setReceiptError("");
        // Снимок ДО очистки инпута: `input.value = ""` обнуляет тот самый
        // FileList, который пришёл в событии, и разбирать было бы уже нечего.
        // А очистка нужна, иначе повторный выбор того же файла не вызовет
        // onChange и трейдер решит, что интерфейс завис.
        const chosen = Array.from(files);
        if (inputRef.current) inputRef.current.value = "";

        const accepted: ReceiptDraft[] = [];
        const rejected: string[] = [];
        for (const file of chosen) {
            const reason = rejectFile(file);
            if (reason) {
                rejected.push(reason);
                continue;
            }
            accepted.push(toDraft(file));
        }
        if (rejected.length > 0) setReceiptError(rejected.join(" "));
        if (accepted.length === 0) return;

        setReceipts((previous) => [...previous, ...accepted]);
    };

    const clearReceipt = (index: number) => {
        setReceipts((previous) => {
            const dropped = previous[index];
            if (dropped) URL.revokeObjectURL(dropped.objectUrl);
            return previous.filter((_, at) => at !== index);
        });
        setReceiptError("");
        if (inputRef.current) inputRef.current.value = "";
    };

    const handleDownloadExistingReceipt = async () => {
        if (!payment.receiptUrl) return;
        setReceiptError("");
        setDownloadingReceipt(true);
        try {
            await downloadReceipt(payment.receiptUrl, token, `${payment.receiptId ?? payment.paymentId ?? "receipt"}.pdf`);
        } catch (err) {
            setReceiptError(formatActionError(err));
        } finally {
            setDownloadingReceipt(false);
        }
    };

    const openClientReceipt = async (receiptId: string) => {
        setClientReceiptError("");
        setOpeningClientReceipt(receiptId);
        try {
            await downloadClientReceipt(payment.orderId, receiptId, token);
        } catch (err) {
            setClientReceiptError(formatActionError(err));
        } finally {
            setOpeningClientReceipt(null);
        }
    };

    const requestComplete = () => {
        if (!canComplete) return;
        setSubmitError("");
        setConfirmAction("complete");
    };

    const savePayinRequisites = async () => {
        if (!canSaveRequisites || submitting) return;
        setSubmitting(true);
        setSubmitError("");
        try {
            await onSubmit({
                action: "setRequisites",
                payment,
                payinRequisites: normalizePayinRequisites(requisitesDraft),
                payinOrderAmount: choosingAmount && orderAmountKopecks !== null ? orderAmountKopecks : undefined,
            });
            setSubmitting(false);
        } catch (err) {
            setSubmitError(formatActionError(err));
            setSubmitting(false);
        }
    };

    // rememberUploadedReceipt закрепляет за драфтом id, который вернула его
    // загрузка. Повторная отправка (закрытие отказало, обрыв на середине пачки)
    // такой файл уже не заливает — она его предъявляет.
    //
    // Драфт ищется по своему File, а не по номеру в списке: список — состояние
    // этой панели, и номер перестал бы значить то же самое, стоило трейдеру
    // убрать файл, пока идёт загрузка.
    const rememberUploadedReceipt = (file: File, receiptId: string) => {
        setReceipts((previous) =>
            previous.map((draft) => (draft.file === file ? {...draft, receiptId} : draft))
        );
    };

    // finishSubmit закрывает подтверждение после удачной отправки.
    //
    // Раньше здесь не сбрасывалось ничего, и это было верно: успех уводил заявку
    // из списка активных, карточка размонтировалась вместе с модалкой, и
    // сбрасывать было нечего. У заявки-обмена это перестало быть правдой — успех
    // рублей открывает второй этап, и карточка намеренно остаётся открытой.
    // Невыключенный флаг оставлял поверх неё модалку с вечным «Отправляем…» и
    // гасил все кнопки: снаружи это выглядело как зависший интерфейс.
    //
    // Там, где карточка всё-таки размонтируется, вызов безвреден.
    const finishSubmit = () => {
        setSubmitting(false);
        setConfirmAction(null);
    };

    const confirmCurrentAction = async () => {
        // Отмена заявки сюда не приходит: у неё своя модалка и свой отправитель
        // (confirmCancelOrder). Отказ здесь — сужение типа, а не защита от
        // состояния: подтвердить её одним кликом нельзя.
        if (!confirmAction || confirmAction === "cancelOrder" || submitting) return;
        // Отправка USDT собирается отдельно: у неё нет ни суммы, ни курса, ни
        // чеков — только hash, — и класть её в общий сборщик значило бы
        // отправлять серверу поля, которых он по этой ручке не ждёт.
        if (confirmAction === "payoutSent") {
            setSubmitting(true);
            setSubmitError("");
            try {
                await onSubmit({action: "payoutSent", payment, txHash: payoutTxHash.trim()});
                finishSubmit();
            } catch (err) {
                setSubmitError(formatActionError(err));
                setSubmitting(false);
            }
            return;
        }
        const request = buildActionRequest(
            payment, confirmAction, amountAnalysis, rateAnalysis, receipts, rememberUploadedReceipt);
        setSubmitting(true);
        setSubmitError("");
        try {
            await onSubmit(request);
            finishSubmit();
        } catch (err) {
            setSubmitError(formatActionError(err));
            setSubmitting(false);
        }
    };

    // confirmCancelOrder отправляет отмену и ОСТАВЛЯЕТ модалку открытой при
    // отказе: три отказа сверки исправляются прямо в ней. Закрыть её значило бы
    // заставить трейдера набирать оба поля заново — то есть превратить защиту от
    // ошибки в источник ошибок.
    const confirmCancelOrder = async () => {
        if (submitting) return;
        if (!cancelOrderConfirmReady(cancelRequisiteRequired, cancelLast4, cancelAmountInput)) return;
        setSubmitting(true);
        setSubmitError("");
        try {
            await onSubmit(buildCancelOrderRequest(
                payment,
                receipts,
                // Пустая строка у заявки без реквизита — не «поле забыли»: сервер
                // как раз требует её пустой, потому что сверять нечему.
                cancelRequisiteRequired ? cancelLast4 : "",
                cancelAmountInput,
                rememberUploadedReceipt,
            ));
            // Отмена сегодня всегда уводит заявку из списка, и карточка
            // размонтируется сама. Сброс здесь всё равно стоит: на него больше
            // не полагаются нигде, и следующий сценарий, оставляющий карточку
            // открытой, не повторит зависшую модалку.
            finishSubmit();
        } catch (err) {
            setSubmitError(formatCancelOrderError(err));
            setSubmitting(false);
        }
    };

    return (
        <>
            <div className="details-backdrop">
                <aside className="details-drawer">
                    <header className="details-header">
                        <div className="details-title-row">
                            <span className={`details-dir-badge ${payment.side}`}>{directionLabel(payment.side)}</span>
                            <div className="details-title-main">
                                <div className="details-amount-line">
                                    <strong>{traderAmountRangeText(payment) ?? formatAmount(payment.status === "success" && payment.externalCompletion ? payment.amountReal ?? payment.amount : getRemainingAmount(payment))}</strong>
                                    <span>RUB</span>
                                    {/* Статус и остаток — одна плашка, пока идёт
                                        отсчёт: подпись у него то же слово PENDING, и
                                        отдельный чип рядом написал бы статус дважды.
                                        Как только отсчёта нет — спор, успех, отмена —
                                        чип возвращается своим цветом. */}
                                    {!workDeadline && (
                                        <span className={`payment-status ${statusClass(payment.status)}`}>
                                            {payment.status === "success" && payment.externalCompletion ? "Local success" : payment.status || "pending"}
                                        </span>
                                    )}
                                    {/* Срок площадки идёт за заявкой и сюда: он течёт с
                                        момента, когда заявка появилась у них, и не
                                        останавливается ни на взятии, ни на апелляции —
                                        именно в апелляции знать остаток важнее всего.
                                        У обычных заявок этого поля нет, и здесь
                                        показывается их собственный срок. */}
                                    {sourceDeadline ? (
                                        <CountdownBadge
                                            label={SOURCE_DEADLINE_LABEL}
                                            expiresAt={sourceDeadline}
                                            onExpire={onExpire}
                                            expiredText="срок вышел"
                                            hint="Срок площадки: сколько осталось до момента, к которому считает её кабинет"
                                            withSeconds
                                        />
                                    ) : (
                                        workDeadline && (
                                            <CountdownBadge
                                                label={ORDER_DEADLINE_LABEL}
                                                expiresAt={workDeadline}
                                                onExpire={onExpire}
                                                expiredText="срок вышел"
                                                hint="Срок заявки: сколько осталось до момента, к которому она должна быть оплачена"
                                                withSeconds
                                            />
                                        )
                                    )}
                                    {claimTimeoutAppeal && (
                                        <span className="payment-status appeal">Срок истёк · Апелляция</span>
                                    )}
                                </div>
                                <div className="details-meta-line">
                                    <span className="mono">{payment.orderId}</span>
                                    <span className="mono">{payment.remoteId}</span>
                                    <span>{formatDate(payment.createdAt)}</span>
                                </div>
                            </div>
                        </div>
                        <button className="details-close" onClick={onClose} aria-label="Закрыть">
                            x
                        </button>
                    </header>

                    <div className="details-scroll">
                        {payment.paymentId && <ExternalCompletionDetails paymentId={payment.paymentId} completion={payment.externalCompletion} token={token}/>}
                        {/* Первым, до всего остального: если площадка закрыла
                            заявку, читать инструкции по её закрытию бессмысленно
                            (В-31). Ниже все три кнопки выключены, и здесь
                            сказано почему и чем именно она закрыта. */}
                        {closedAtSource && (
                            <div className="details-lock-hint" role="status">{closedAtSource}</div>
                        )}
                        {claimTimeoutAppeal && (
                            <div className="details-lock-hint" role="status">
                                Срок истёк. Автоматическая отмена запрещена: требуется решение трейдера или администратора.
                            </div>
                        )}
                        <section className="details-section">
                            <SectionTitle hint={payment.bankId}>{isIncome ? "Приём платежа" : "Закрытие заявки"}</SectionTitle>
                            <PaymentProgress payment={payment} isIncome={isIncome}/>
                        </section>
                        {payment.side === "in" && !payment.payinParts && <FailureEvidenceList orderId={payment.orderId} operator hideEmpty/>}
                        {payment.merchantPortalIn && !payment.payinParts && <PortalReceiptList orderId={payment.orderId} audience="operator" title="Чеки мерчанта" hideEmpty/>}

                        <section className="details-section">
                            <SectionTitle hint="только чтение">{isIncome ? "Данные плательщика" : "Реквизиты клиента"}</SectionTitle>
                            <div className="details-info-card">
                                <InfoRow label="Мерчант" value={payment.merchantName || "—"}/>
                                {fixedRateLabel && (
                                    <InfoRow label="Курс заявки" value={fixedRateLabel} mono/>
                                )}
                                <InfoRow label="ФИО" value={payment.clientFio}/>
                                <InfoRow label="Карта / счёт" value={formatCard(payment.clientCard)} mono/>
                                <InfoRow label="Телефон" value={payment.clientPhone || "-" } mono/>
                                <InfoRow label="Банк" value={payment.clientBank || payment.bankId || "-"}/>
                                <InfoRow label="Назначение" value="Перевод по реквизитам заявки"/>
                            </div>
                        </section>

                        <WorkflowStages
                            workflowKind={payment.workflowKind}
                            context={{
                                payment,
                                onConfirmPart: async (partNumber, receiptId, partRevision) => {
                                    if (!payment.payinParts) return;
                                    await onSubmit({action: "confirmPart", payment, payinPartConfirmation: {partNumber, receiptId, partRevision, requisitesVersion: payment.payinParts.requisitesVersion}});
                                },
                                onReplacePart: async (partNumber, partRevision, requisites, requestId) => {
                                    if (!payment.payinParts) return;
                                    await onSubmit({action: "replacePart", payment, payinPartReplacement: {partNumber, partRevision, requisites, requestId, requisitesVersion: payment.payinParts.requisitesVersion}});
                                },
                                requisites: {
                                    draft: requisitesDraft,
                                    onChange: setRequisitesDraft,
                                    onSave: savePayinRequisites,
                                    submitting,
                                    canSave: canSaveRequisites,
                                    terms: payinTerms,
                                    total: requisitesTotal,
                                    orderAmount: choosingAmount
                                        ? {value: orderAmountInput, onChange: changeOrderAmount, problem: orderAmountIssue}
                                        : null,
                                },
                                receipt: {
                                    inputRef,
                                    drafts: receipts,
                                    error: receiptError,
                                    downloadingExisting: downloadingReceipt,
                                    onFiles: handleFiles,
                                    onClear: clearReceipt,
                                    onDownloadExisting: handleDownloadExistingReceipt,
                                    disabled: sellReviewBlocking,
                                },
                                clientReceipt: {
                                    onOpen: (receiptId: string) => { void openClientReceipt(receiptId); },
                                    opening: openingClientReceipt,
                                    error: clientReceiptError,
                                },
                                amount: {
                                    value: amountInput,
                                    onChange: setAmountInput,
                                    analysis: amountAnalysis,
                                },
                                rate: {
                                    value: rateInput,
                                    onChange: setRateInput,
                                    analysis: rateAnalysis,
                                    fixed: fixedRate,
                                },
                                complete: {
                                    // «Рубли получены» вместо общего «Деньги
                                    // получены»: у заявки-обмена денег две ноги,
                                    // и кнопка закрывает ровно рублёвую.
                                    label: exchangeBuy ? "Рубли получены" : actionLabel,
                                    onRequest: requestComplete,
                                    canRequest: canComplete,
                                    blockedReason: canComplete ? "" : disabledReason,
                                    submitting,
                                },
                                exchangePayout: {
                                    txHash: payoutTxHash,
                                    onTxHashChange: setPayoutTxHash,
                                    onSend: () => {
                                        setSubmitError("");
                                        setConfirmAction("payoutSent");
                                    },
                                    submitting,
                                    blockedReason: payoutBlocked,
                                },
                                sell: {
                                    state: activeSellState,
                                    publishing: publishingSellAddress,
                                    onPublish: () => { void publishSellAddress(); },
                                    error: sellPublishError,
                                },
                            }}
                        />
                    </div>

                    <footer className="details-actions">
                        {submitError && (
                            <div className="details-action-error">{submitError}</div>
                        )}
                        {payoutStageOpen ? (
                            <div className="details-lock-hint" role="status">
                                Рубли получены и проведены. Осталось отправить USDT на адрес клиента
                                и вставить hash перевода — заявка закроется после этого.
                            </div>
                        ) : exchangeBuy ? (
                            // У заявки-обмена причина отказа стоит в самом
                            // этапе, рядом с его кнопкой. Второй копией внизу
                            // она бы только спорила с первой.
                            <div className="details-lock-hint" role="status">
                                Основные действия живут внутри своих этапов. Здесь остаются
                                только выходы из заявки.
                            </div>
                        ) : !canComplete && (
                            <div className="details-lock-hint">{disabledReason}</div>
                        )}
                        <div className="details-action-row">
                            {exchangeSell && <SellDisputeButton key={payment.paymentId ?? payment.orderId} paymentId={payment.paymentId ?? payment.orderId} onOpened={onExpire}/>}
                            {/* Рубли по заявке с открытым USDT-этапом уже
                                закрыты: закрывать их второй раз нечем, и
                                кнопка уходит вовсе, а не гаснет.
                                У заявки-обмена её здесь нет и до этого: этапы
                                идут лентой, и каждый закрывается своей кнопкой
                                внутри блока — рубли во втором, USDT в третьем.
                                Внизу остаются только выходы из заявки. */}
                            {!payoutStageOpen && !exchangeBuy && (
                                <button
                                    className="btn btn-success details-main-action"
                                    onClick={requestComplete}
                                    disabled={!canComplete || submitting}
                                    title={!canComplete ? disabledReason : ""}
                                >
                                    {actionLabel}
                                </button>
                            )}
                            {!exchangeSell && !claimTimeoutAppeal && !payoutStageOpen && (
                                <button
                                    className="btn btn-danger"
                                    onClick={() => { setSubmitError(""); setConfirmAction("cancel"); }}
                                    disabled={!supportedWorkflow || submitting || Boolean(returnToPoolBlocked)}
                                    title={returnToPoolBlocked}
                                >
                                    Вернуть в пул
                                </button>
                            )}
                            {/* Метка спора живёт на заявке и стоит ровно один
                                раз: второе нажатие бэкенд молча проглотит, не
                                изменив ничего. Кнопка гаснет заранее, чтобы не
                                обещать действия, которого уже нет; соседние две
                                при этом остаются рабочими — спор не блокирует
                                ни закрытие, ни возврат в пул (В-27).
                                Заявка, закрытая на площадке, гасит и её: спорить
                                там уже не о чем (В-31). */}
                            {!exchangeSell && !claimTimeoutAppeal && (
                                <button
                                    className="btn btn-appeal"
                                    onClick={() => { setSubmitError(""); setConfirmAction("appeal"); }}
                                    disabled={(terminalPayment && !payoutStageOpen) || !supportedWorkflow || submitting || payment.hasAppeal || Boolean(closedAtSource)}
                                    title={closedAtSource || (payment.hasAppeal ? "Спор по этой заявке уже открыт — её разбирает админ" : undefined)}
                                >
                                    {payment.hasAppeal ? "Спор открыт" : "Открыть спор"}
                                </button>
                            )}
                            {/* Четвёртое действие и единственное необратимое из
                                трёх нижних: заявка умирает, мерчант получает
                                отказ, заявке кабинета уходит отмена на площадку.
                                Слово «отмена» здесь честное — в отличие от
                                соседней кнопки, которая заявку лишь отпускает.
                                Метка спора её не гасит (условие ДМ №1). */}
                            <button
                                className="btn btn-danger"
                                onClick={() => {
                                    setSubmitError("");
                                    setCancelLast4("");
                                    setCancelAmountInput("");
                                    setConfirmAction("cancelOrder");
                                }}
                                disabled={exchangeSell || !supportedWorkflow || Boolean(exchangeDataMissing) || submitting || payoutStageOpen || Boolean(cancelOrderBlocked)}
                                title={payoutStageOpen ? PAYOUT_STAGE_CANCEL_HINT : (exchangeDataMissing || cancelOrderBlocked)}
                            >
                                Отменить заявку
                            </button>
                        </div>
                    </footer>
                </aside>
            </div>

            {confirmAction === "cancelOrder" ? (
                <CancelOrderModal
                    payment={payment}
                    isIncome={isIncome}
                    requisiteRequired={cancelRequisiteRequired}
                    last4={cancelLast4}
                    onLast4Change={setCancelLast4}
                    amountInput={cancelAmountInput}
                    onAmountChange={setCancelAmountInput}
                    submitting={submitting}
                    error={submitError}
                    onClose={() => { if (!submitting) setConfirmAction(null); }}
                    onConfirm={confirmCancelOrder}
                />
            ) : confirmAction && (
                <ConfirmModal
                    action={confirmAction}
                    payment={payment}
                    isIncome={isIncome}
                    amountAnalysis={amountAnalysis}
                    rateAnalysis={rateAnalysis}
                    submitting={submitting}
                    error={submitError}
                    onClose={() => { if (!submitting) setConfirmAction(null); }}
                    onConfirm={confirmCurrentAction}
                />
            )}
        </>
    );
}

// Текст, объясняющий, почему «Отменить заявку» выключено на открытом
// USDT-этапе. Отдельной константой, а не строкой в разметке: причина одна, а
// читает её и кнопка, и подсказка.
const PAYOUT_STAGE_CANCEL_HINT =
    "Рубли по заявке уже проведены — отменить её нельзя. Отправьте USDT или откройте спор.";

function InfoRow({label, value, mono}: { label: string; value: string | number | null | undefined; mono?: boolean }): JSX.Element {
    return (
        <div className="details-info-row">
            <span>{label}</span>
            <strong className={mono ? "mono" : ""}>{value == null || value === "" ? "-" : value}</strong>
        </div>
    );
}

// PaymentProgress показывает остаток и пожелание мерчанта по числу платежей.
//
// Индикатор «платёж 2 из 3» описывал механику дробления, которой больше нет
// (UOF-02): заявку закрывает один платёж на весь остаток. Настройка мерчанта
// осталась в API и стала рекомендацией — трейдер её видит, система по ней
// ничего не запрещает.
function PaymentProgress({payment, isIncome}: { payment: Payment; isIncome: boolean }): JSX.Element {
    const remaining = getRemainingAmount(payment);
    const original = payment.amount;
    const maxPayments = payment.maxPayments ?? 0;
    const hasLimit = maxPayments > 0;
    // Условия мерчанта по входящей заявке. Пока сумма не выбрана, остаток —
    // вилка диапазона, а не его верхняя граница, которую держит сервер.
    const terms = isIncome ? traderPayinTerms(payment) : null;
    const termsLines = traderTermsLines(terms);
    const remainingText = terms?.range
        ? `${formatRubles(terms.range.min)}–${formatRubles(terms.range.max)} ₽`
        : `${formatAmount(remaining)} ₽`;

    return (
        <div className="details-progress-card">
            <div className="details-progress-top">
                <div>
                    <span>{isIncome ? "Остаток к получению" : "Остаток к оплате"}</span>
                    <strong>{remainingText}</strong>
                    {original !== remaining && <small>из {formatAmount(original)} ₽</small>}
                </div>
                <div>
                    <span>Закрытие</span>
                    <strong>{payment.payinParts ? "По подтверждённым частям" : "Одним платежом"}</strong>
                    <small>{payment.payinParts ? "неоплаченные части требуют доказательства отказа" : "на весь остаток"}</small>
                </div>
            </div>
            {termsLines.map((line) => (
                <div className="details-lock-hint" role="status" key={line}>{line}</div>
            ))}
            {hasLimit && !terms && (
                <div className="details-lock-hint">
                    Мерчант просит закрыть заявку в {maxPayments} платеж(а/ей) — это рекомендация, закрытие она не
                    ограничивает.
                </div>
            )}
        </div>
    );
}

function ConfirmModal({
                          action,
                          payment,
                          isIncome,
                          amountAnalysis,
                          rateAnalysis,
                          submitting,
                          error,
                          onClose,
                          onConfirm,
                      }: {
    // Отмена заявки сюда не приходит: у неё своя модалка с вводом, и её нельзя
    // подтвердить одним нажатием. Тип это и говорит.
    action: Exclude<ConfirmAction, "cancelOrder">;
    payment: Payment;
    isIncome: boolean;
    amountAnalysis: AmountAnalysis;
    rateAnalysis: RateAnalysis;
    submitting: boolean;
    error: string;
    onClose: () => void;
    onConfirm: () => void;
}): JSX.Element {
    const copy = getConfirmCopy(action, amountAnalysis, isIncome);

    return (
        <div className="confirm-backdrop" onClick={onClose}>
            <div className="confirm-modal" onClick={(event) => event.stopPropagation()}>
                <div className={`confirm-icon ${copy.variant}`}>{copy.icon}</div>
                <h3>{copy.title}</h3>
                <p>{copy.body}</p>
                {action === "complete" && payment.payinParts && !amountAnalysis.full && <p className="details-lock-hint">Заявка будет окончательно закрыта на {formatAmount(amountAnalysis.amount)} RUB. Неоплаченные {formatAmount(amountAnalysis.remaining - (amountAnalysis.amount ?? 0))} RUB подтверждены отказами и не вернутся в пул.</p>}
                <div className="confirm-summary">
                    <InfoRow label="Заявка" value={payment.orderId} mono/>
                    {/* У отправки USDT реквизит рублёвой части не при чём: она
                        закрыта, и человек сверяет перед необратимым переводом
                        совсем другую пару — адрес клиента и сумму в USDT. */}
                    {action === "payoutSent" ? (
                        <>
                            <InfoRow label="Адрес клиента" value={payment.exchange?.payoutAddress} mono/>
                            <InfoRow label="Сеть" value={payment.exchange?.payoutNetwork} mono/>
                            <InfoRow label="К отправке" value={`${payment.exchange?.payoutAmount ?? "-"} USDT`} mono/>
                            <InfoRow label="Новый статус" value={copy.result}/>
                        </>
                    ) : (
                        <>
                            <InfoRow
                                label={isIncome ? "Реквизит приёма" : "Реквизит"}
                                value={formatCard(isIncome ? payment.payinRequisites?.cardNumber : payment.clientCard)}
                                mono
                            />
                            {action === "complete" ? (
                                <>
                                    <InfoRow label="Сумма перевода" value={`${formatAmount(amountAnalysis.amount)} RUB`} mono/>
                                    <InfoRow label="Курс" value={`${rateAnalysis.normalized ?? "-"} RUB/USDT`} mono/>
                                    <InfoRow label="Результат" value={copy.result}/>
                                </>
                            ) : (
                                <InfoRow label="Новый статус" value={copy.result}/>
                            )}
                        </>
                    )}
                </div>
                {error && <div className="confirm-error">{error}</div>}
                <div className="confirm-actions">
                    <button className="btn btn-ghost" onClick={onClose} disabled={submitting}>Назад</button>
                    <button className={`btn ${copy.buttonClass}`} onClick={onConfirm} disabled={submitting}>
                        {submitting ? "Отправляем…" : copy.button}
                    </button>
                </div>
            </div>
        </div>
    );
}

// CancelOrderModal — единственная модалка панели, которую нельзя подтвердить
// одним кликом, и это её предмет, а не украшение.
//
// Прежний запрет трейдерской отмены снят владельцем 28.08.2026, и заменён он не
// отсутствием кнопки, а ценой подтверждения: файл-пруф у платежа плюс два поля,
// которые трейдер обязан переписать с самой заявки. Ошибиться ими случайно
// нельзя — можно только не глядя переписать не ту заявку, и тогда откажет
// сервер.
//
// Поля именно два и именно эти. Сумма — полная сумма заявки, а не остаток: по
// отменяемой заявке оплат не было, и «полная» здесь единственное честное слово.
// Реквизит показывается, только когда он у заявки есть; у заявки приёма до
// ввода реквизитов и у выплаты по СБП сверять нечего, и просить человека
// вводить то, с чем нечему сойтись, — способ получить отказ на ровном месте.
//
// Модалка не закрывается по отказу: отказы сверки исправляются здесь же.
function CancelOrderModal({
                              payment,
                              isIncome,
                              requisiteRequired,
                              last4,
                              onLast4Change,
                              amountInput,
                              onAmountChange,
                              submitting,
                              error,
                              onClose,
                              onConfirm,
                          }: {
    payment: Payment;
    isIncome: boolean;
    requisiteRequired: boolean;
    last4: string;
    onLast4Change: (next: string) => void;
    amountInput: string;
    onAmountChange: (next: string) => void;
    submitting: boolean;
    error: string;
    onClose: () => void;
    onConfirm: () => void;
}): JSX.Element {
    const ready = cancelOrderConfirmReady(requisiteRequired, last4, amountInput);

    return (
        <div className="confirm-backdrop" onClick={onClose}>
            <div className="confirm-modal" onClick={(event) => event.stopPropagation()}>
                <div className="confirm-icon danger">!</div>
                <h3>Отменить заявку</h3>
                <p>
                    Заявка будет отменена окончательно, мерчант получит отказ. Это действие
                    необратимо.
                </p>
                <div className="confirm-summary">
                    <InfoRow label="Заявка" value={payment.orderId} mono/>
                    <InfoRow
                        label={isIncome ? "Реквизит приёма" : "Реквизит"}
                        value={formatCard(isIncome ? payment.payinRequisites?.cardNumber : payment.clientCard)}
                        mono
                    />
                    <InfoRow label="Новый статус" value="ЗАЯВКА → ОТМЕНЕНА"/>
                </div>
                <div className="requisites-form">
                    <div className="requisites-grid">
                        {requisiteRequired && (
                            <label className="requisites-field">
                                <span>Последние 4 цифры реквизита *</span>
                                <input
                                    className="mono"
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={4}
                                    autoComplete="off"
                                    value={last4}
                                    // Нецифры отсекаются на вводе: сервер их всё равно не
                                    // примет, и молчаливый отказ по опечатке в разделителе
                                    // был бы отказом ни о чём.
                                    onChange={(event) => onLast4Change(event.target.value.replace(/\D/g, "").slice(0, 4))}
                                />
                            </label>
                        )}
                        <label className="requisites-field">
                            <span>Сумма заявки, ₽ *</span>
                            <input
                                className="mono"
                                type="text"
                                inputMode="decimal"
                                autoComplete="off"
                                value={amountInput}
                                onChange={(event) => onAmountChange(event.target.value.replace(/[^\d.,\s]/g, ""))}
                            />
                        </label>
                    </div>
                </div>
                {error && <div className="confirm-error">{error}</div>}
                <div className="confirm-actions">
                    <button className="btn btn-ghost" onClick={onClose} disabled={submitting}>Назад</button>
                    <button className="btn btn-danger" onClick={onConfirm} disabled={submitting || !ready}>
                        {submitting ? "Отменяем…" : "Отменить заявку"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function getConfirmCopy(action: Exclude<ConfirmAction, "cancelOrder">, amountAnalysis: AmountAnalysis, isIncome: boolean): {
    title: string;
    body: string;
    result: string;
    button: string;
    buttonClass: string;
    variant: string;
    icon: string;
} {
    if (action === "cancel") {
        // Прежний текст обещал, что «заявка будет помечена как отменённая», —
        // и это было неправдой для любой заявки, внешней и обычной. Заявка
        // возвращается в пул и достаётся следующему трейдеру. Слова «отмена»
        // здесь нет вовсе: трейдер отпускает заявку, а не убивает её, и
        // внутренняя строка платежа — не его предмет.
        return {
            title: "Подтвердите возврат заявки в пул",
            body: amountAnalysis.currentPayment > 1
                ? "Заявка вернётся в пул с непогашенным остатком и станет доступна другим трейдерам. Уже оплаченные части остаются засчитанными."
                : "Заявка вернётся в пул и станет доступна другим трейдерам. Вы просто перестаёте её вести.",
            result: "ЗАЯВКА → В ПУЛ",
            button: "Вернуть в пул",
            buttonClass: "btn-danger",
            variant: "danger",
            icon: "!",
        };
    }
    if (action === "payoutSent") {
        // Единственное подтверждение панели, которое предупреждает не о нашем
        // статусе, а о чужой сети: наш отказ обратим, перевод в TRON — нет.
        return {
            title: "Подтвердите отправку USDT",
            body: "Проверьте адрес и сумму: перевод в сети необратим. Мы записываем только факт отправки — вернуть его нечем.",
            result: "USDT-ЭТАП → ЗАКРЫТ",
            button: "USDT отправлены",
            buttonClass: "btn-success",
            variant: "success",
            icon: "✓",
        };
    }
    if (action === "appeal") {
        return {
            title: "Подтвердите открытие спора",
            body: "Заявка будет переведена в спорный статус и потребует отдельного разбора.",
            result: "APPEAL",
            button: "Открыть спор",
            buttonClass: "btn-appeal",
            variant: "appeal",
            icon: "?",
        };
    }

    return {
        title: isIncome ? "Подтвердите получение" : "Подтвердите выполнение",
        body: isIncome
            ? "Вы подтверждаете, что деньги реально поступили на указанные реквизиты и приложено корректное подтверждение. Это действие необратимо."
            : "Вы подтверждаете, что реально провели платёж через свой банк и приложили корректный чек-доказательство. Это действие необратимо.",
        result: "Заявка закрывается полностью",
        button: "Подтвердить",
        buttonClass: "btn-success",
        variant: "success",
        icon: "✓",
    };
}
